import { useCallback, useMemo, useState } from 'react'

import type { ParagraphFeedback } from './content-brief.interface'

export interface CommentBlockMeta {
  index: number
  textContent: string
}

export interface UseContentBriefCommentsProps {
  blockList: CommentBlockMeta[]
}

export interface UseContentBriefCommentsReturn {
  commentMap: Record<number, string>
  feedbackList: ParagraphFeedback[]
  commentCount: number
  handleCommentChange: (blockIndex: number, comment: string | undefined) => void
  resetComments: () => void
}

export const useContentBriefComments = ({
  blockList,
}: UseContentBriefCommentsProps): UseContentBriefCommentsReturn => {
  const [commentMap, setCommentMap] = useState<Record<number, string>>({})

  const feedbackList = useMemo(
    (): ParagraphFeedback[] =>
      blockList
        .filter((b) => commentMap[b.index])
        .map((b) => ({
          lineNumber: b.index + 1,
          text: b.textContent,
          comment: commentMap[b.index],
        })),
    [blockList, commentMap]
  )

  const commentCount = feedbackList.length

  const handleCommentChange = useCallback((blockIndex: number, comment: string | undefined) => {
    setCommentMap((prev) => {
      const next = { ...prev }

      if (!comment) {
        delete next[blockIndex]
        return next
      }

      next[blockIndex] = comment
      return next
    })
  }, [])

  const resetComments = useCallback(() => {
    setCommentMap({})
  }, [])

  return {
    commentMap,
    feedbackList,
    commentCount,
    handleCommentChange,
    resetComments,
  }
}
